import React, { useEffect, useState } from 'react'
import Layout from '../../../components/Layout/Layout'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import useIsMobile from '../../../hooks/useIsMobile'
import { useAuth } from '../../../context/auth'
import { Modal } from 'react-bootstrap'

const MainConferenceRecord = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isMobile = useIsMobile()
  const [eventName, setEventName] = useState('')
  const [salesRecords, setSalesRecords] = useState([])
  const [totalSaleQty, setTotalSaleQty] = useState(0)
  const [totalSaleAmount, setTotalSaleAmount] = useState(0)
  const [loading, setLoading] = useState(false)

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingOrderId, setEditingOrderId] = useState(null)
  const [editingItems, setEditingItems] = useState([])
  const [editRemark, setEditRemark] = useState('')
  const [saving, setSaving] = useState(false)

  const [auth] = useAuth()

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await axios.get(
          `/api/v1/main/conference/records/${id}`
        )
        if (response.data.success) {
          setEventName(response.data.conference.event)
          setSalesRecords(response.data.salesRecords)
          setTotalSaleQty(response.data.totalQty || 0)
          setTotalSaleAmount(response.data.totalAmount || 0)
        } else {
          toast.error(response.data.message || 'Failed to fetch data.')
        }
      } catch (error) {
        console.error('Error fetching data:', error.message)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchData()
    }
  }, [id])

  const groupByOrderId = (records) => {
    return records.reduce((grouped, record) => {
      if (!grouped[record.conferenceOrderId]) {
        grouped[record.conferenceOrderId] = []
      }
      grouped[record.conferenceOrderId].push(record)
      return grouped
    }, {})
  }

  const groupedRecords = groupByOrderId(salesRecords)

  const recalculateTotals = (records) => {
    const filteredRecords = records.filter((record) => record.status !== 2)
    const newTotalQty = filteredRecords.reduce(
      (sum, record) => sum + record.qty,
      0
    )
    const newTotalAmount = filteredRecords.reduce(
      (sum, record) => sum + record.qty * record.price,
      0
    )
    setTotalSaleQty(newTotalQty)
    setTotalSaleAmount(newTotalAmount)
  }

  const handleVoid = (conferenceOrderId) => {
    if (window.confirm('Are you sure you want to void this order?')) {
      voidSalesRecord(conferenceOrderId)
    }
  }

  const voidSalesRecord = async (conferenceOrderId) => {
    try {
      const response = await axios.put(`/api/v1/main/conference/record/void`, {
        conferenceData: id,
        conferenceOrderId,
      })
      if (response.data.success) {
        setSalesRecords((prevRecords) => {
          const updatedRecords = prevRecords.map((record) =>
            record.conferenceOrderId === conferenceOrderId
              ? { ...record, status: 2 }
              : record
          )
          recalculateTotals(updatedRecords)
          return updatedRecords
        })
        toast.success('Order voided successfully')
      } else {
        toast.error(response.data.message || 'Failed to void order.')
      }
    } catch (error) {
      console.error('Error voiding record:', error.message)
      toast.error('Error voiding order.')
    }
  }

  // Edit handlers
  const handleEdit = (conferenceOrderId) => {
    const orderRecords = salesRecords.filter(
      (r) => r.conferenceOrderId === conferenceOrderId
    )
    setEditingOrderId(conferenceOrderId)
    setEditingItems(orderRecords.map((r) => ({ ...r, originalQty: r.qty })))
    setEditRemark(orderRecords[0]?.remark || '')
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setEditingOrderId(null)
    setEditingItems([])
    setEditRemark('')
  }

  const handleQtyChange = (recordId, newQty) => {
    setEditingItems((prev) =>
      prev.map((item) =>
        item._id === recordId
          ? { ...item, qty: Math.max(1, parseInt(newQty) || 1) }
          : item
      )
    )
  }

  const handleDeleteItem = (recordId) => {
    if (editingItems.length === 1) {
      toast.error(
        'Cannot delete the last item. Use Void to remove the entire order.'
      )
      return
    }
    setEditingItems((prev) => prev.filter((item) => item._id !== recordId))
  }

  const handleSaveEdit = async () => {
    try {
      setSaving(true)

      // Find items to delete (in original but not in editingItems)
      const originalItems = salesRecords.filter(
        (r) => r.conferenceOrderId === editingOrderId
      )
      const editingItemIds = editingItems.map((item) => item._id)
      const itemsToDelete = originalItems.filter(
        (item) => !editingItemIds.includes(item._id)
      )

      // Find items with changed qty
      const itemsToUpdate = editingItems.filter((item) => {
        const original = originalItems.find((o) => o._id === item._id)
        return original && original.qty !== item.qty
      })

      // Delete removed items
      for (const item of itemsToDelete) {
        await axios.delete(
          `/api/v1/main/conference/record/delete-item/${item._id}`
        )
      }

      // Update changed quantities
      for (const item of itemsToUpdate) {
        await axios.put(
          `/api/v1/main/conference/record/update-item/${item._id}`,
          {
            qty: item.qty,
          }
        )
      }

      // Update remark if changed
      const originalRemark = originalItems[0]?.remark || ''
      if (editRemark !== originalRemark) {
        await axios.put(`/api/v1/main/conference/record/update-remark`, {
          conferenceData: id,
          conferenceOrderId: editingOrderId,
          remark: editRemark,
        })
      }

      // Update local state
      setSalesRecords((prev) => {
        // Remove deleted items
        let updated = prev.filter(
          (r) =>
            r.conferenceOrderId !== editingOrderId ||
            editingItemIds.includes(r._id)
        )

        // Update qty and remark
        updated = updated.map((r) => {
          if (r.conferenceOrderId === editingOrderId) {
            const editedItem = editingItems.find((e) => e._id === r._id)
            return editedItem
              ? { ...r, qty: editedItem.qty, remark: editRemark }
              : r
          }
          return r
        })

        recalculateTotals(updated)
        return updated
      })

      toast.success('Order updated successfully')
      handleCloseEditModal()
    } catch (error) {
      console.error('Error saving edit:', error.message)
      toast.error('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  const getEditingOrderTotal = () => {
    return editingItems.reduce((sum, item) => sum + item.qty * item.price, 0)
  }

  const getEditingOrderQty = () => {
    return editingItems.reduce((sum, item) => sum + item.qty, 0)
  }

  const handleCancel = () => navigate('/main/conference')
  const handleReturnHome = () => navigate('/main/dashboard')

  return (
    <Layout title={'Conference Record'}>
      <div className={'pt-3 ' + (isMobile ? 'mx-1' : 'mx-3')}>
        <div
          className={`d-flex mb-2 ${
            isMobile
              ? 'flex-column'
              : 'justify-content-between align-items-center'
          }`}
        >
          {isMobile && (
            <div className='d-flex justify-content-end w-100'>
              <button
                type='button'
                className='btn btn-secondary btn-sm me-3'
                onClick={handleCancel}
              >
                List
              </button>
              <button
                type='button'
                className='btn btn-success btn-sm me-3'
                onClick={() => navigate(`/main/conference/sale/${id}`)}
              >
                Sale
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                onClick={handleReturnHome}
              >
                <i className='bi bi-house-fill'></i>
              </button>
            </div>
          )}

          <div
            className={`w-100 ${isMobile ? 'mt-2' : ''}`}
            style={{ textAlign: isMobile ? 'left' : 'inherit' }}
          >
            <h5 className='mb-0' style={{ color: '#556B2F' }}>
              {eventName || 'Loading...'}
            </h5>
          </div>

          {!isMobile && (
            <div className='d-flex justify-content-end w-100'>
              <button
                type='button'
                className='btn btn-secondary btn-sm me-3'
                onClick={handleCancel}
              >
                List
              </button>
              <button
                type='button'
                className='btn btn-success btn-sm me-3'
                onClick={() => navigate(`/main/conference/sale/${id}`)}
              >
                Sale
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                onClick={handleReturnHome}
              >
                <i className='bi bi-house-fill'></i>
              </button>
            </div>
          )}
        </div>

        {/* Total Summary */}
        <div className='fw-bold mb-2'>
          Total Sale: RM {totalSaleAmount.toFixed(2)} ({totalSaleQty} Qty)
        </div>

        {/* Desktop Table View */}
        {!isMobile && (
          <table className='table'>
            <thead>
              <tr>
                <th scope='col'>Order ID</th>
                <th className='text-center'>Total Qty</th>
                <th className='text-end'>Total (RM)</th>
                <th>Remark</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan='5' className='text-center py-4'>
                    <div
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                    >
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                    Loading...
                  </td>
                </tr>
              ) : Object.keys(groupedRecords).length > 0 ? (
                Object.keys(groupedRecords)
                  .sort((a, b) => b - a)
                  .map((orderId) => {
                    const records = groupedRecords[orderId]
                    const firstRecord = records[0]
                    const totalQty = records.reduce(
                      (sum, record) => sum + record.qty,
                      0
                    )
                    const totalAmount = records.reduce(
                      (sum, record) => sum + record.qty * record.price,
                      0
                    )

                    return (
                      <React.Fragment key={orderId}>
                        <tr
                          className={`conf-highlight ${
                            firstRecord.status === 2 ? 'text-muted' : ''
                          }`}
                        >
                          <td>{String(orderId).padStart(3, '0')}</td>
                          <td className='text-center'>{totalQty}</td>
                          <td className='text-end'>{totalAmount.toFixed(2)}</td>
                          <td>{firstRecord.remark || '-'}</td>
                          <td className='text-end'>
                            {firstRecord.status === 2 ? (
                              <span className='text-secondary'>Voided</span>
                            ) : (
                              <>
                                <i
                                  className='bi bi-pencil-fill text-primary me-3'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleEdit(orderId)}
                                ></i>
                                <i
                                  className='bi bi-trash-fill text-danger'
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleVoid(orderId)}
                                ></i>
                              </>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan='5'>
                            {records.map((record, index) => (
                              <div key={record._id}>
                                {`${index + 1}. `}
                                <span className='me-2'>{record.itemCode}</span>
                                {record.description}
                                <span className='mx-1'>
                                  x {record.qty}
                                </span> RM {record.price.toFixed(2)}
                              </div>
                            ))}
                          </td>
                        </tr>
                      </React.Fragment>
                    )
                  })
              ) : (
                <tr>
                  <td colSpan='5' className='text-center'>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* Mobile List View */}
        {isMobile && (
          <div className='mt-2'>
            {loading ? (
              <div className='text-center py-4'>
                <div
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                >
                  <span className='visually-hidden'>Loading...</span>
                </div>
                Loading...
              </div>
            ) : Object.keys(groupedRecords).length > 0 ? (
              Object.keys(groupedRecords)
                .sort((a, b) => b - a)
                .map((orderId) => {
                  const records = groupedRecords[orderId]
                  const firstRecord = records[0]
                  const totalQty = records.reduce(
                    (sum, record) => sum + record.qty,
                    0
                  )
                  const totalAmount = records.reduce(
                    (sum, record) => sum + record.qty * record.price,
                    0
                  )
                  const isVoided = firstRecord.status === 2

                  return (
                    <div
                      key={orderId}
                      className={`py-2 ${isVoided ? 'text-muted' : ''}`}
                      style={{ borderBottom: '1px solid #dee2e6' }}
                    >
                      {/* Row 1: Order ID & Actions */}
                      <div className='d-flex justify-content-between align-items-center'>
                        <span className='fw-bold' style={{ color: '#0c432a' }}>
                          #{String(orderId).padStart(3, '0')}
                        </span>
                        <div>
                          {isVoided ? (
                            <span className='text-secondary small'>Voided</span>
                          ) : (
                            <>
                              <i
                                className='bi bi-pencil-fill text-primary me-3'
                                style={{ cursor: 'pointer', fontSize: '1rem' }}
                                onClick={() => handleEdit(orderId)}
                              ></i>
                              <i
                                className='bi bi-trash-fill text-danger'
                                style={{ cursor: 'pointer', fontSize: '1rem' }}
                                onClick={() => handleVoid(orderId)}
                              ></i>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Row 2: Items */}
                      <div className='mt-1'>
                        {records.map((record, index) => (
                          <div key={record._id} className='small'>
                            {`${index + 1}. ${record.description} x${
                              record.qty
                            } `}
                            <span className='text-muted'>
                              RM {record.price.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Row 3: Total & Remark */}
                      <div className='d-flex justify-content-between align-items-center mt-1'>
                        <small className='text-muted'>
                          {firstRecord.remark || ''}
                        </small>
                        <span className='fw-semibold'>
                          RM {totalAmount.toFixed(2)} ({totalQty})
                        </span>
                      </div>
                    </div>
                  )
                })
            ) : (
              <div className='text-center py-4 text-muted'>
                No records found.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        centered
        size={isMobile ? 'sm' : 'lg'}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Order #{String(editingOrderId).padStart(3, '0')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Items List */}
          {editingItems.map((item, index) => (
            <div
              key={item._id}
              className={`${
                isMobile ? 'py-2' : 'd-flex align-items-center py-2'
              }`}
              style={{ borderBottom: '1px solid #eee' }}
            >
              {/* Desktop Layout */}
              {!isMobile && (
                <>
                  <div className='flex-grow-1'>
                    <div className='fw-semibold'>{item.description}</div>
                    <small className='text-muted'>
                      {item.itemCode} | RM {item.price.toFixed(2)}
                    </small>
                  </div>
                  <div className='d-flex align-items-center'>
                    <button
                      className='btn btn-outline-secondary btn-sm'
                      onClick={() => handleQtyChange(item._id, item.qty - 1)}
                      disabled={item.qty <= 1}
                    >
                      -
                    </button>
                    <input
                      type='number'
                      className='form-control form-control-sm mx-2 text-center'
                      style={{ width: '60px' }}
                      value={item.qty}
                      onChange={(e) =>
                        handleQtyChange(item._id, e.target.value)
                      }
                      min='1'
                    />
                    <button
                      className='btn btn-outline-secondary btn-sm'
                      onClick={() => handleQtyChange(item._id, item.qty + 1)}
                    >
                      +
                    </button>
                    <span
                      className='mx-3 fw-semibold'
                      style={{ minWidth: '80px' }}
                    >
                      RM {(item.qty * item.price).toFixed(2)}
                    </span>
                    <button
                      className='btn btn-outline-danger btn-sm'
                      onClick={() => handleDeleteItem(item._id)}
                      title='Remove item'
                    >
                      <i className='bi bi-trash'></i>
                    </button>
                  </div>
                </>
              )}

              {/* Mobile Layout */}
              {isMobile && (
                <>
                  <div className='d-flex justify-content-between align-items-start'>
                    <div>
                      <div style={{ fontWeight: 500 }}>{item.description}</div>
                      <small className='text-muted'>
                        RM {item.price.toFixed(2)} each
                      </small>
                    </div>
                    <i
                      className='bi bi-trash text-danger'
                      style={{
                        cursor: 'pointer',
                        marginLeft: '5px',
                        fontSize: '1rem',
                      }}
                      onClick={() => handleDeleteItem(item._id)}
                    ></i>
                  </div>{' '}
                  <div className='d-flex justify-content-between align-items-center mt-2'>
                    <div className='d-flex align-items-center'>
                      <button
                        className='btn btn-outline-secondary btn-sm'
                        onClick={() => handleQtyChange(item._id, item.qty - 1)}
                        disabled={item.qty <= 1}
                      >
                        -
                      </button>
                      <input
                        type='number'
                        className='form-control form-control-sm mx-2 text-center'
                        style={{ width: '50px' }}
                        value={item.qty}
                        onChange={(e) =>
                          handleQtyChange(item._id, e.target.value)
                        }
                        min='1'
                      />
                      <button
                        className='btn btn-outline-secondary btn-sm'
                        onClick={() => handleQtyChange(item._id, item.qty + 1)}
                      >
                        +
                      </button>
                    </div>
                    <span className='fw-semibold'>
                      RM {(item.qty * item.price).toFixed(2)}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Remark */}
          <div className='mt-3'>
            <label className='form-label fw-semibold'>Catatan</label>
            <input
              type='text'
              className='form-control'
              value={editRemark}
              onChange={(e) => setEditRemark(e.target.value)}
            />
          </div>

          {/* Order Total */}
          <div className='mt-3 d-flex justify-content-between'>
            <span className='fw-bold'>Total:</span>
            <span className='fw-bold'>
              RM {getEditingOrderTotal().toFixed(2)} ({getEditingOrderQty()}{' '}
              items)
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className='btn btn-secondary btn-sm me-4'
            onClick={handleCloseEditModal}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className='btn btn-primary btn-sm'
            onClick={handleSaveEdit}
            disabled={saving || editingItems.length === 0}
          >
            {saving ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                ></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </Layout>
  )
}

export default MainConferenceRecord
